'use client'

import { useState, useEffect, useRef } from 'react'
import { RootContext } from '@/modules/contextService'
import { IUser } from '@/interfaces/IUser'
import tokenService from '@/modules/tokenService/client'
import { PATHWAY } from '@/modules/httpService'
import cookieService from '@/modules/cookieService'
import { usePathname } from 'next/navigation'

export interface RootProviderProps {
  children: React.ReactNode
}

export interface IRootData {
  user?: IUser | null
  loading?: boolean
  theme?: string
}

function RootProvider ({ children }: RootProviderProps) {
  const [rootData, setRootData] = useState<IRootData>({ user: undefined, loading: true, theme: '' })
  // const { user } = rootData
  const [loading, setLoading] = useState(false)
  const [navigating, setNavigating] = useState(false)
  const pathname = usePathname()
  const prevPathname = useRef(pathname)

  async function getUserData () {
    setLoading(true)

    const res = await tokenService.setUser(
      rootData,
      setRootData
    )
    setRootData({ ...rootData, user: res.user, loading: false })

    setLoading(false)
  }

  function loadThemeFromCookie () {
    const storedTheme = cookieService.getCookie('theme')

    if (!storedTheme) {
      cookieService.setCookie('theme', storedTheme || 'dark')
    }
    if (pathname !== '/') {
      document.documentElement.setAttribute('data-theme', storedTheme || 'dark')
      setRootData({
        ...rootData,
        theme: storedTheme || 'dark'
      })
    } else {
      document.documentElement.setAttribute('data-theme', 'dark')
      setRootData({
        ...rootData,
        theme: 'dark'
      })
    }
  }

  async function checkReferral () {
    const referrer = document.referrer

    // determine if the pathname is the same as the referrer
    const matches = window.location.pathname === referrer

    if (referrer && !matches) {
      try {
        console.log('posting referrer')
        const slug = window.location.pathname
        await PATHWAY.post('/referrers', {
          referrer,
          slug
        })
      } catch (err) {
        console.error(err)
      }
    }
  }

  useEffect(() => {
    async function loadUser () {
      if (!loading) {
        await Promise.allSettled([
          getUserData(),
          checkReferral()
        ])
      }
    }
    loadThemeFromCookie()
    loadUser()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hide overlay when navigation completes
  useEffect(() => {
    if (prevPathname.current !== pathname) {
      setNavigating(false)
      prevPathname.current = pathname
    }
  }, [pathname])

  // Show overlay immediately on same-origin link clicks
  useEffect(() => {
    function handleClick (e: MouseEvent) {
      const anchor = (e.target as HTMLElement).closest('a')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return
      if (href.startsWith('http') && !href.startsWith(window.location.origin)) return
      setNavigating(true)
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  return (
    <RootContext.Provider value={{ rootData, setRootData }}>
      {children}
      {navigating && (
        <>
          <style>{`
            @keyframes nav-bar {
              0%   { transform: translateX(-100%); }
              100% { transform: translateX(500%); }
            }
            .nav-loader {
              position: fixed;
              top: 0; left: 0; right: 0;
              height: 3px;
              background: transparent;
              z-index: 9999;
              overflow: hidden;
            }
            .nav-loader::after {
              content: '';
              position: absolute;
              height: 100%;
              width: 25%;
              background: linear-gradient(90deg, transparent, var(--primary, #1976d2), transparent);
              animation: nav-bar 0.8s ease-in-out infinite;
            }
          `}</style>
          <div className="nav-loader" />
        </>
      )}
    </RootContext.Provider>
  )
}

export default RootProvider
